// qs-store-silhouette.ts
import type { Silhouette, SilhouetteCategory } from '@/types/qs-type-silhouette'
import { DEFAULT_SILHOUETTE_COLOR, DEFAULT_SILHOUETTE_VISIBILITY, SilhouetteCategory as SilhouetteCategoryEnum } from '@/types/qs-type-silhouette'
import { useCustomImageUpload } from '@/composables/qs-useCustomImageUpload'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

export const useSilhouetteStore = defineStore('silhouette', () => {
  const currentSilhouetteId = useStorage('silhouette-current-id', 'woman-model-01')
  const isVisible = useStorage('silhouette-visible', DEFAULT_SILHOUETTE_VISIBILITY)
  const color = useStorage('silhouette-color', DEFAULT_SILHOUETTE_COLOR)
  const availableSilhouettes = ref<Silhouette[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const customImageUpload = useCustomImageUpload()

  const allSilhouettes = computed(() => {
    const silhouettes = [...availableSilhouettes.value]
    const customSilhouette = customImageUpload.getCustomImageAsSilhouette()
    if (customSilhouette) {
      silhouettes.unshift(customSilhouette)
    }
    return silhouettes
  })

  const currentSilhouette = computed(() =>
    allSilhouettes.value.find(s => s.id === currentSilhouetteId.value) || null
  )

  const silhouettesByCategory = computed(() => {
    const categories: Record<SilhouetteCategory, Silhouette[]> = {
      [SilhouetteCategoryEnum.PEOPLE]: [],
      [SilhouetteCategoryEnum.VEHICLES]: [],
      [SilhouetteCategoryEnum.ANIMALS]: [],
      [SilhouetteCategoryEnum.OBJECTS]: [],
      [SilhouetteCategoryEnum.BUILDINGS]: [],
      [SilhouetteCategoryEnum.CUSTOM]: []
    }

    allSilhouettes.value.forEach(silhouette => {
      const category = (silhouette.isCustom ? SilhouetteCategoryEnum.CUSTOM : silhouette.category) as SilhouetteCategory
      if (category && categories[category]) {
        categories[category].push(silhouette)
      }
    })

    return categories
  })

  const hasSilhouettes = computed(() => allSilhouettes.value.length > 0)
  const hasCustomImage = computed(() => customImageUpload.hasCustomImage.value)
  const isCustomImageActive = computed(() => currentSilhouette.value?.isCustom === true)

  const setCurrentSilhouette = (silhouette: Silhouette | null) => {
    currentSilhouetteId.value = silhouette ? silhouette.id : ''
  }

  const setVisibility = (visible: boolean) => {
    isVisible.value = visible
  }

  const toggleVisibility = () => {
    isVisible.value = !isVisible.value
  }

  const setColor = (newColor: string) => {
    color.value = newColor
  }

  const loadSilhouettes = async () => {
    if (isLoading.value) return

    isLoading.value = true
    error.value = null

    try {
      const { getSilhouettes } = await import('@/assets/silhouettes/index')
      const silhouettes = await getSilhouettes()

      availableSilhouettes.value = silhouettes

      if (!currentSilhouetteId.value && silhouettes.length > 0) {
        currentSilhouetteId.value = silhouettes[0].id
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load silhouettes'
    } finally {
      isLoading.value = false
    }
  }

  const getSilhouetteById = (id: string): Silhouette | null => {
    return availableSilhouettes.value.find(s => s.id === id) || null
  }

  const getSilhouettesByCategory = (category: SilhouetteCategory): Silhouette[] => {
    return silhouettesByCategory.value[category] || []
  }

  const uploadCustomImage = async (): Promise<boolean> => {
    const success = await customImageUpload.selectCustomImage()
    if (success) {
      const customSilhouette = customImageUpload.getCustomImageAsSilhouette()
      if (customSilhouette) {
        setCurrentSilhouette(customSilhouette)
      }
    }
    return success
  }

  const removeCustomImage = () => {
    const wasCustomActive = isCustomImageActive.value
    customImageUpload.removeCustomImage()

    if (wasCustomActive && availableSilhouettes.value.length > 0) {
      setCurrentSilhouette(availableSilhouettes.value[0])
    }
  }

  const resetToDefaults = () => {
    currentSilhouetteId.value = 'woman-model-01'
    isVisible.value = DEFAULT_SILHOUETTE_VISIBILITY
    color.value = DEFAULT_SILHOUETTE_COLOR
  }

  watch(
    () => allSilhouettes.value,
    (newSilhouettes) => {
      if (newSilhouettes.length > 0 && !currentSilhouette.value) {
        currentSilhouetteId.value = newSilhouettes[0].id
      }
    },
    { immediate: true }
  )

  watch(
    () => customImageUpload.hasCustomImage.value,
    (hasCustom) => {
      if (!hasCustom && isCustomImageActive.value) {
        if (availableSilhouettes.value.length > 0) {
          setCurrentSilhouette(availableSilhouettes.value[0])
        }
      }
    }
  )

  const cleanup = () => {
    availableSilhouettes.value = []
    isLoading.value = false
    error.value = null
    customImageUpload.cleanup()
  }

  return {
    currentSilhouetteId,
    isVisible,
    color,
    availableSilhouettes,
    isLoading,
    error,
    allSilhouettes,
    currentSilhouette,
    silhouettesByCategory,
    hasSilhouettes,
    hasCustomImage,
    isCustomImageActive,
    setCurrentSilhouette,
    setVisibility,
    toggleVisibility,
    setColor,
    loadSilhouettes,
    getSilhouetteById,
    getSilhouettesByCategory,
    uploadCustomImage,
    removeCustomImage,
    resetToDefaults,
    cleanup,
    customImageUpload
  }
})